import { useEffect, useState, type ReactNode } from 'react';
import type { ClientGateways } from '../gateway/index.ts';
import { useI18n, type MessageKey } from '../i18n/index.ts';
import { Dialog } from './Dialog.tsx';

/**
 * Illustrated counting rules a player confirms before playing (after Play / on entering the game). The
 * pictures show the real rule: a vehicle counts when its middle (bottom-centre) crosses the line in the
 * arrow's direction inside the counted minute; car, bus, truck and motorcycle count 1 each; what the camera
 * cannot see is not counted. Acceptance is stored per player by the server ("don't show again"); the demo
 * keeps it in this browser.
 */
const DEMO_KEY = 'urbanrats.rulesIntro.v1';
const DEMO_VERSION = 'intro-1:demo';

// ---- tiny scene kit (viewBox 0 0 160 100) ---------------------------------------------------------------
const Road = () => (
  <>
    <rect x="0" y="0" width="160" height="100" rx="10" className="ri-bg" />
    <rect x="0" y="18" width="160" height="64" className="ri-road" />
    <line x1="0" y1="50" x2="160" y2="50" className="ri-lane" />
  </>
);
/** Yellow gate: zone left of the line, line at x, chevrons pointing right. */
const Gate = ({ x = 100 }: { x?: number }) => (
  <>
    <rect x={x - 46} y="18" width="46" height="64" className="ri-zone" />
    {[x - 36, x - 22].map((cx) => (
      <polyline key={cx} points={`${cx},38 ${cx + 8},50 ${cx},62`} className="ri-chev" />
    ))}
    <line x1={x} y1="16" x2={x} y2="84" className="ri-line" />
  </>
);
const Car = ({ x, y, flip = false, ghost = false }: { x: number; y: number; flip?: boolean; ghost?: boolean }) => (
  <g transform={`translate(${x} ${y}) ${flip ? 'scale(-1 1)' : ''}`} className={ghost ? 'ri-ghost' : 'ri-car'}>
    <rect x="-14" y="-7" width="28" height="14" rx="4" />
    <rect x="-4" y="-5.5" width="9" height="11" rx="2" className="ri-glass" />
  </g>
);
const Truck = ({ x, y }: { x: number; y: number }) => (
  <g transform={`translate(${x} ${y})`} className="ri-truck">
    <rect x="-26" y="-9" width="38" height="18" rx="2" />
    <rect x="13" y="-8" width="12" height="16" rx="3" className="ri-cab" />
  </g>
);
const Mid = ({ x, y }: { x: number; y: number }) => <circle cx={x} cy={y} r="3" className="ri-mid" />;
const Badge = ({ x, y, ok, text }: { x: number; y: number; ok: boolean; text?: string }) => (
  <g transform={`translate(${x} ${y})`} className={ok ? 'ri-ok' : 'ri-no'}>
    <circle r="9" />
    <text y="3.5">{text ?? (ok ? '✓' : '✕')}</text>
  </g>
);

const SCENES: { key: string; art: ReactNode }[] = [
  {
    key: 'direction',
    art: (
      <svg viewBox="0 0 160 100" aria-hidden>
        <Road />
        <Gate />
        <Car x={124} y={34} />
        <Badge x={149} y={34} ok />
        <Car x={64} y={66} flip />
        <Badge x={36} y={66} ok={false} />
      </svg>
    ),
  },
  {
    key: 'middle',
    art: (
      <svg viewBox="0 0 160 100" aria-hidden>
        <Road />
        <Gate />
        <Car x={86} y={36} />
        <Mid x={86} y={43} />
        <Car x={104} y={66} />
        <Mid x={104} y={73} />
        <Badge x={130} y={66} ok text="+1" />
        <Badge x={58} y={30} ok={false} text="0" />
      </svg>
    ),
  },
  {
    key: 'big',
    art: (
      <svg viewBox="0 0 160 100" aria-hidden>
        <Road />
        <Gate />
        <Truck x={124} y={36} />
        <Badge x={124} y={36} ok text="1" />
        <g transform="translate(126 66)" className="ri-moto">
          <rect x="-9" y="-3" width="18" height="6" rx="3" />
        </g>
        <Badge x={146} y={66} ok text="1" />
      </svg>
    ),
  },
  {
    key: 'hidden',
    art: (
      <svg viewBox="0 0 160 100" aria-hidden>
        <Road />
        <Gate />
        <Car x={118} y={30} ghost />
        <Truck x={118} y={44} />
        <Badge x={146} y={26} ok={false} />
      </svg>
    ),
  },
  {
    key: 'others',
    art: (
      <svg viewBox="0 0 160 100" aria-hidden>
        <Road />
        <Gate />
        <g transform="translate(116 36)" className="ri-person">
          <circle cy="-8" r="3.5" />
          <rect x="-2.5" y="-4" width="5" height="12" rx="2" />
        </g>
        <g transform="translate(116 68)" className="ri-bike">
          <circle cx="-7" r="5" />
          <circle cx="7" r="5" />
          <line x1="-7" y1="0" x2="7" y2="0" />
        </g>
        <Badge x={142} y={36} ok={false} />
        <Badge x={142} y={68} ok={false} />
      </svg>
    ),
  },
  {
    key: 'time',
    art: (
      <svg viewBox="0 0 160 100" aria-hidden>
        <rect x="0" y="0" width="160" height="100" rx="10" className="ri-bg" />
        <circle cx="52" cy="52" r="26" className="ri-clock" />
        <line x1="52" y1="52" x2="52" y2="32" className="ri-hand" />
        <line x1="52" y1="52" x2="66" y2="58" className="ri-hand" />
        <rect x="46" y="20" width="12" height="5" rx="2" className="ri-hand-cap" />
        <text x="112" y="46" className="ri-big">60</text>
        <text x="112" y="62" className="ri-small">sn / s</text>
        <text x="112" y="84" className="ri-small">↺</text>
      </svg>
    ),
  },
];

export function RulesIntro({ gw, onLeave }: { gw: ClientGateways; onLeave?: (() => void) | undefined }) {
  const { t } = useI18n();
  const [state, setState] = useState<{ version: string; show: boolean } | null>(null);
  const [dontShow, setDontShow] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const t0 = setTimeout(() => {
      if (gw.rulesIntro) {
        void gw.rulesIntro.get().then((r) => {
          // Server unreachable: show the rules anyway (acceptance is retried on the button).
          if (live) setState(r ?? { version: '', show: true });
        });
      } else {
        const seen = (() => {
          try {
            return localStorage.getItem(DEMO_KEY) === DEMO_VERSION;
          } catch {
            return false;
          }
        })();
        if (live) setState({ version: DEMO_VERSION, show: !seen });
      }
    }, 0);
    return () => {
      live = false;
      clearTimeout(t0);
    };
  }, [gw]);

  if (!state?.show) return null;
  const accept = async () => {
    setBusy(true);
    setErr(null);
    if (gw.rulesIntro) {
      const cur = state.version || (await gw.rulesIntro.get())?.version || '';
      const r = await gw.rulesIntro.accept(cur, dontShow);
      setBusy(false);
      if (!r.ok) {
        if (r.error === 'RULES_CHANGED') {
          const fresh = await gw.rulesIntro.get();
          if (fresh) setState(fresh);
        }
        return setErr(t('ri.error'));
      }
    } else {
      try {
        if (dontShow) localStorage.setItem(DEMO_KEY, DEMO_VERSION);
        else localStorage.removeItem(DEMO_KEY);
      } catch {
        /* convenience only */
      }
      setBusy(false);
    }
    setState({ ...state, show: false });
  };
  return (
    <Dialog open title={t('ri.title')} onClose={() => undefined} variant="modal" dismissible={false} testId="rules-intro">
      <p className="ri-lead">{t('ri.lead')}</p>
      <ol className="ri-grid">
        {SCENES.map((s) => (
          <li key={s.key} className="ri-card" data-testid={`ri-${s.key}`}>
            <div className="ri-art">{s.art}</div>
            <strong>{t(`ri.${s.key}.title` as MessageKey)}</strong>
            <p>{t(`ri.${s.key}.text` as MessageKey)}</p>
          </li>
        ))}
      </ol>
      <div className="ri-actions">
        <label className="ri-check">
          <input type="checkbox" checked={dontShow} onChange={(e) => setDontShow(e.target.checked)} data-testid="ri-dontshow" /> {t('ri.dontShow')}
        </label>
        {err && (
          <p role="alert" className="ri-err">
            {err}
          </p>
        )}
        <div className="ri-buttons">
          {onLeave && (
            <button type="button" className="ghost-btn" onClick={onLeave}>
              {t('ri.leave')}
            </button>
          )}
          <button type="button" className="confirm" disabled={busy} onClick={() => void accept()} data-testid="ri-accept">
            {t('ri.accept')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
